import { useEffect, useRef, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HealthResponseSchema,
  PERMISSION_KEYS,
  type DayPlanKitFlag,
  type DayPlanVisitDto,
  type HealthResponse,
  type TrialListQuery,
} from '@ctms/contracts';
import {
  AlertTriangle,
  Boxes,
  Building2,
  Contact,
  DollarSign,
  FlaskConical,
  Hospital,
  LayoutDashboard,
  LogOut,
  Shield,
  ShieldAlert,
  Users,
} from 'lucide-react';

import { apiClient } from '../api/client.js';
import { API_PATHS } from '../api/paths.js';
import { type UseSessionResult } from '../features/auth/use-session.js';
import { ActiveTrialPicker } from '../features/trials/active-trial-picker/active-trial-picker.js';
import { BarManagerDialog } from '../features/cockpit/bar-manager/bar-manager-dialog.js';
import { TrialBars } from '../features/cockpit/trial-bars/trial-bars.js';
import { TrialSearchDialog } from '../features/cockpit/trial-search/trial-search-dialog.js';
import {
  useCockpitState,
  useSaveCockpitState,
} from '../features/cockpit/trial-bars/use-trial-bars-query.js';
import { trialsApi } from '../features/trials/trials.api.js';
import { trialQueryKeys } from '../features/trials/trials.query-keys.js';
import { TrialParticipants } from '../features/trials/trial-participants/trial-participants.js';
import { ContactsCatalog } from '../features/personnel/contacts-catalog/contacts-catalog.js';
import { PatientPanel } from '../features/patients/patient-panel/patient-panel.js';
import { patientQueryKeys } from '../features/patients/patients.query-keys.js';
import { DayPlan } from '../features/visits/day-plan/day-plan.js';
import { visitQueryKeys } from '../features/visits/visits.query-keys.js';
import { ProcedureRegistration } from '../features/visits/procedure-registration/procedure-registration.js';
import {
  VisitRegistration,
  type VisitRegistrationTarget,
} from '../features/visits/visit-registration/visit-registration.js';
import { KitRegistrationDialog } from '../features/kits/kit-registration/kit-registration.js';
import {
  VclPrintDialog,
  type VclPrintTarget,
} from '../features/documents/vcl-print/vcl-print.js';
import {
  KitDestructionPrintDialog,
  type KitDestructionPrintTarget,
} from '../features/documents/kit-destruction-print/kit-destruction-print.js';
import {
  PatientTodoPrintDialog,
  type PatientTodoPrintTarget,
} from '../features/documents/patient-todo-print/patient-todo-print.js';
import { KitInventoryView } from '../features/kits/kit-inventory/kit-inventory-view.js';
import { KitDemandForecastView } from '../features/kits/kit-demand-forecast/kit-demand-forecast-view.js';
import { TrialsView } from '../features/trials/trials-view.js';
import { UsersView } from '../features/users/users-view.js';
import { SiteStatusCard } from '../features/tenants/site-status-card/site-status-card.js';
import { BuildBadge } from '../components/build-badge/build-badge.js';
import { BUILD_INFO } from '../build-info.js';

export interface CockpitPageProps {
  session: UseSessionResult;
}

type CockpitTab =
  | 'dashboard'
  | 'trials'
  | 'contacts'
  | 'users'
  | 'finance'
  | 'inventory'
  | 'demand';

export interface KitRegistrationVisitTarget {
  visitId: number;
  visitLabel: string;
  patientName: string;
  patientCode: string;
  initialFlag?: DayPlanKitFlag | null;
}

// #7 A7: the SAME shape as the ActiveTrialPicker query → an identical query
// key, so the restore validation shares one cached fetch with the picker.
const ACTIVE_TRIALS_QUERY: TrialListQuery = { page: 1, limit: 100, active: true };

export function CockpitPage({ session }: CockpitPageProps): JSX.Element {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<CockpitTab>('dashboard');
  // Trial the whole cockpit works on (REQ-VIS-005). Shared by the tabs, picked in the header.
  const [activeTrialId, setActiveTrialId] = useState<number | null>(null);
  // Person picked in the cockpit (participant list or site chart) — the chart bar
  // (element 6) and the visit table (element 9) show this person in the active trial.
  const [selectedParticipantPatientId, setSelectedParticipantPatientId] = useState<number | null>(null);
  // Visit whose data registration window is open (REQ-VIS-018..028). Opened from
  // the day plan (a booked visit) or from the patient chart (ANY visit of the
  // plan, including one never booked - REQ-VIS-021).
  const [registrationVisit, setRegistrationVisit] = useState<VisitRegistrationTarget | null>(null);
  // Visit whose procedure registration is open (REQ-VIS-030), entered from the registration window.
  const [procedureVisit, setProcedureVisit] = useState<DayPlanVisitDto | null>(null);
  // Visit whose kit registration is open (REQ-KIT-014..026, opened from DayPlan or ChartParticipation).
  const [kitVisitTarget, setKitVisitTarget] = useState<KitRegistrationVisitTarget | null>(null);
  // Visit whose VCL print dialog is open (REQ-DOC-007, Domain 7 — opened from DayPlan).
  const [vclVisitTarget, setVclVisitTarget] = useState<VclPrintTarget | null>(null);
  // Visit whose patient todo card dialog is open (REQ-DOC-017, Domain 7 — opened from DayPlan).
  const [patientTodoTarget, setPatientTodoTarget] = useState<PatientTodoPrintTarget | null>(null);
  const [kitDestructionTarget, setKitDestructionTarget] =
    useState<KitDestructionPrintTarget | null>(null);
  // #7 A6: the loupe — trial search dialog opened from the bars row.
  const [trialSearchOpen, setTrialSearchOpen] = useState(false);
  // #7 A5: the grid — bar definition window opened from the bars row.
  const [barManagerOpen, setBarManagerOpen] = useState(false);
  // Transient confirmation after a kit registration was persisted (REQ-KIT-013..022).
  const [kitSavedNotice, setKitSavedNotice] = useState<string | null>(null);
  useEffect(() => {
    if (kitSavedNotice === null) {
      return undefined;
    }
    const timer = setTimeout(() => setKitSavedNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [kitSavedNotice]);
  // Day-plan row of the open registration target - the procedures entry
  // (REQ-VIS-030) hangs on the day-plan flow; the chart entry has no row.
  const registrationDayPlanRow = registrationVisit?.dayPlanRow ?? null;

  const health = useQuery({
    queryKey: [API_PATHS.health],
    queryFn: async (): Promise<HealthResponse> => {
      const raw: unknown = await apiClient.get(API_PATHS.health).json();
      return HealthResponseSchema.parse(raw);
    },
    retry: false,
  });

  const { user, tenant, permissions, isTenantAdmin, hasFinanceAccess, logout } = session;
  const canReadTrials = permissions.includes(PERMISSION_KEYS.TRIALS_READ);
  const canWriteTrials = permissions.includes(PERMISSION_KEYS.TRIALS_WRITE);
  const canReadPersonnel = permissions.includes(PERMISSION_KEYS.PERSONNEL_READ);
  const canWritePersonnel = permissions.includes(PERMISSION_KEYS.PERSONNEL_WRITE);
  const canReadPatients = permissions.includes(PERMISSION_KEYS.PATIENTS_READ);
  const canWritePatients = permissions.includes(PERMISSION_KEYS.PATIENTS_WRITE);
  const canReadVisits = permissions.includes(PERMISSION_KEYS.VISITS_READ);
  const canWriteVisits = permissions.includes(PERMISSION_KEYS.VISITS_WRITE);
  // Domain 7 (REQ-DOC-007): any site staff may print the visit check-list.
  const canReadDocuments = permissions.includes(PERMISSION_KEYS.DOCUMENTS_READ);
  const canWriteKits = permissions.includes(PERMISSION_KEYS.KITS_WRITE);

  // #7 (Piotr odp.4): the cockpit remembers the active trial server-side, so
  // the recall follows the user across workstations. Saved on every switch
  // (including a clear to "— brak —"); the restore-on-load side lands in A7.
  const saveCockpitState = useSaveCockpitState();
  const handleSelectTrial = (trialId: number | null): void => {
    setActiveTrialId(trialId);
    // A trial switch invalidates the person picked in the previous trial's list.
    setSelectedParticipantPatientId(null);
    saveCockpitState.mutate(trialId);
  };

  // #7 A7 (Piotr odp.4): restore the remembered trial on load — only while it
  // is still ACTIVE; a deactivated recall leaves the cockpit at "— brak —"
  // (frozen: the no-trial screen behaviour). Runs once, never overrides a
  // trial the user picked before the recall arrived.
  const cockpitState = useCockpitState(canReadVisits);
  const activeTrials = useQuery({
    queryKey: trialQueryKeys.list(ACTIVE_TRIALS_QUERY),
    queryFn: () => trialsApi.listTrials(ACTIVE_TRIALS_QUERY),
    enabled: canReadTrials,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) {
      return;
    }
    const lastTrialId = cockpitState.data?.lastTrialId ?? null;
    const items = activeTrials.data?.items;
    if (lastTrialId === null || items === undefined) {
      return;
    }
    restoredRef.current = true;
    setActiveTrialId((current) => {
      if (current !== null) {
        return current; // the user picked first — the recall does not fight it
      }
      return items.some((trial) => trial.id === lastTrialId) ? lastTrialId : null;
    });
  }, [cockpitState.data, activeTrials.data]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Top Application Bar — Fixed Desktop Cockpit 1920x1080 (ADR-005) */}
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-900/90 backdrop-blur-md px-6 py-2.5">
        <div className="mx-auto flex max-w-[1920px] items-center justify-between gap-4">
          {/* Brand & Active Site Context */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
                <Hospital className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-sm font-bold tracking-tight text-slate-100">
                    CTMS Cockpit
                  </h1>
                  <BuildBadge />
                </div>
                <p className="text-[10px] text-slate-400 leading-none">
                  Ośrodki Badań Klinicznych
                </p>
              </div>
            </div>

            {/* Active Site Context (REQ-CORE-001, REQ-CORE-003) */}
            <div
              data-testid="active-site-badge"
              className="flex items-center gap-1.5 rounded-full border border-cyan-800/60 bg-cyan-950/30 px-3 py-1 text-xs text-cyan-300"
            >
              <Building2 className="h-3.5 w-3.5 text-cyan-400" />
              <span className="font-medium">{tenant?.name ?? `Ośrodek #${user?.tenantId}`}</span>
            </div>

            {/* Active Trial Context (REQ-VIS-005) — one trial for the whole cockpit, next to the site badge. */}
            <ActiveTrialPicker
              canRead={canReadTrials}
              activeTrialId={activeTrialId}
              onSelect={(trial) => handleSelectTrial(trial?.id ?? null)}
            />
            {/* Wariant A (#7): the favorite-bars row — trial buttons switch the
                active trial instantly; the grid opens the bar definition window. */}
            <TrialBars
              canRead={canReadVisits}
              activeTrialId={activeTrialId}
              onSelectTrial={handleSelectTrial}
              onOpenGrid={() => setBarManagerOpen(true)}
              onOpenSearch={() => setTrialSearchOpen(true)}
            />
          </div>

          {/* User Profile & Logout */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs font-medium text-slate-100 flex items-center justify-end gap-1.5">
                <span>{user?.displayName}</span>
                {isTenantAdmin ? (
                  <span
                    data-testid="role-badge-admin"
                    className="rounded border border-amber-800/80 bg-amber-950/60 px-1.5 py-0.2 text-[10px] text-amber-300"
                  >
                    Admin
                  </span>
                ) : (
                  <span
                    data-testid="role-badge-user"
                    className="rounded border border-slate-700 bg-slate-800 px-1.5 py-0.2 text-[10px] text-slate-400"
                  >
                    Użytkownik
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 font-mono flex items-center justify-end gap-1">
                <span>{user?.email}</span>
                {hasFinanceAccess ? (
                  <span className="text-emerald-400" title="Dostęp do finansów aktywny">
                    • Finanse
                  </span>
                ) : (
                  <span className="text-slate-500" title="Brak uprawnień finansowych">
                    • Bez finansów
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              aria-label="Wyloguj"
              title="Wyloguj się z systemu"
              className="flex items-center gap-1.5 rounded-md border border-slate-800 bg-slate-850 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800 hover:text-rose-300 transition-colors cursor-pointer"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Wyloguj</span>
            </button>
          </div>
        </div>
      </header>

      {/* Cockpit Layout: Sidebar + Main Content */}
      <div className="flex flex-1 mx-auto w-full max-w-[1920px]">
        {/* Left Navigation Sidebar */}
        <aside
          aria-label="Panel boczny nawigacji"
          className="w-60 shrink-0 border-r border-slate-800 bg-slate-900/60 backdrop-blur-xs flex flex-col justify-between p-3.5 sticky top-[53px] h-[calc(100vh-53px)] overflow-y-auto"
        >
          <div className="space-y-3">
            <div className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Moduły Systemu
            </div>

            <nav aria-label="Nawigacja kokpitu" className="space-y-1">
              <button
                type="button"
                onClick={() => setActiveTab('dashboard')}
                className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                  activeTab === 'dashboard'
                    ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                    : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                }`}
              >
                <LayoutDashboard className={`h-4 w-4 shrink-0 ${activeTab === 'dashboard' ? 'text-cyan-400' : 'text-slate-400'}`} />
                <span>Kokpit Główny</span>
              </button>

              <button
                type="button"
                data-testid="tab-trials"
                onClick={() => setActiveTab('trials')}
                className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                  activeTab === 'trials'
                    ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                    : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                }`}
              >
                <FlaskConical className={`h-4 w-4 shrink-0 ${activeTab === 'trials' ? 'text-cyan-400' : 'text-slate-400'}`} />
                <span>Badania</span>
              </button>

              {activeTrialId !== null && (
                <button
                  type="button"
                  data-testid="tab-inventory"
                  onClick={() => setActiveTab('inventory')}
                  className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                    activeTab === 'inventory'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                  }`}
                >
                  <Boxes className={`h-4 w-4 shrink-0 ${activeTab === 'inventory' ? 'text-cyan-400' : 'text-slate-400'}`} />
                  <span>Magazyn Kitów</span>
                </button>
              )}

              {activeTrialId !== null && (
                <button
                  type="button"
                  data-testid="tab-demand"
                  onClick={() => setActiveTab('demand')}
                  className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                    activeTab === 'demand'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                  }`}
                >
                  <AlertTriangle className={`h-4 w-4 shrink-0 ${activeTab === 'demand' ? 'text-cyan-400' : 'text-slate-400'}`} />
                  <span>Prognoza braków</span>
                </button>
              )}

              {canReadPersonnel && (
                <button
                  type="button"
                  data-testid="tab-contacts"
                  onClick={() => setActiveTab('contacts')}
                  className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                    activeTab === 'contacts'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                  }`}
                >
                  <Contact className={`h-4 w-4 shrink-0 ${activeTab === 'contacts' ? 'text-cyan-400' : 'text-slate-400'}`} />
                  <span>Kontakty</span>
                </button>
              )}

              {/* Login accounts — Administrator Ośrodka only (REQ-CORE-008). Label distinguishes them from the contact catalog. */}
              {isTenantAdmin && (
                <button
                  type="button"
                  data-testid="tab-personnel"
                  onClick={() => setActiveTab('users')}
                  className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                    activeTab === 'users'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                  }`}
                >
                  <Users className={`h-4 w-4 shrink-0 ${activeTab === 'users' ? 'text-cyan-400' : 'text-slate-400'}`} />
                  <span>Konta i uprawnienia</span>
                </button>
              )}

              {/* Financial Module Tab — hidden for Regular User without finance access (REQ-CORE-009, REQ-CORE-010) */}
              {hasFinanceAccess && (
                <button
                  type="button"
                  data-testid="tab-finance"
                  onClick={() => setActiveTab('finance')}
                  className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                    activeTab === 'finance'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 shadow-xs'
                      : 'text-slate-300 hover:bg-slate-800/70 hover:text-slate-100 border border-transparent'
                  }`}
                >
                  <DollarSign className={`h-4 w-4 shrink-0 ${activeTab === 'finance' ? 'text-cyan-400' : 'text-slate-400'}`} />
                  <span>Moduł Finansowy</span>
                </button>
              )}
            </nav>
          </div>

          {/* Sidebar Footer — Status context */}
          <div className="pt-3 border-t border-slate-800/80 text-[11px] text-slate-400 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">System:</span>
              <span className="text-emerald-400 font-mono font-medium flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Online
              </span>
            </div>
            <div className="flex items-center justify-between text-slate-400">
              <span>Widok:</span>
              <span className="font-mono text-slate-400">Desktop</span>
            </div>
          </div>
        </aside>

        {/* Main Cockpit Surface */}
        <main className="flex-1 min-w-0 p-6">
        {activeTab === 'dashboard' && canReadPatients && (
          <div className="space-y-6">
            {/* Element 1 + 4 of the legend: the day plan of the active trial, with the day picker (REQ-VIS-002/004). */}
            <DayPlan
              trialId={activeTrialId}
              canRead={canReadVisits}
              canWrite={canWriteVisits}
              /*
               * The day plan row carries the planned side only - the performed date
               * and hour are not part of DayPlanVisitDto (no GET here). REQ-VIS-017
               * offers REJESTRUJ for a planned visit only, so a row marked registered
               * stands in for its planned day - the same substitution the day plan
               * itself uses for its readiness rule.
               */
              onRegisterVisit={(visit) =>
                setRegistrationVisit({
                  visitId: visit.visitId,
                  visitLabel: `V${visit.visitNumber}`,
                  patientName: `${visit.patientFirstName} ${visit.patientLastName}`,
                  scheduledDate: visit.date,
                  scheduledTime: visit.time,
                  performedDate: visit.status === 'registered' ? visit.date : null,
                  performedTime: visit.status === 'registered' ? visit.time : null,
                  currentType: visit.type,
                  currentComment: visit.mainComment,
                  dayPlanRow: visit,
                })
              }
              onRegisterKits={(visit) =>
                setKitVisitTarget({
                  visitId: visit.visitId,
                  visitLabel: `V${visit.visitNumber}`,
                  patientName: `${visit.patientFirstName} ${visit.patientLastName}`,
                  patientCode: `#${visit.patientId}`,
                  initialFlag: visit.flag,
                })
              }
              onPrintVcl={
                canReadDocuments
                  ? (visit) =>
                      setVclVisitTarget({
                        visitId: visit.visitId,
                        visitLabel: `V${visit.visitNumber}`,
                        patientName: `${visit.patientFirstName} ${visit.patientLastName}`,
                        patientCode: `#${visit.patientId}`,
                      })
                  : undefined
              }
              onPrintPatientTodo={
                canReadDocuments
                  ? (visit) =>
                      setPatientTodoTarget({
                        visitId: visit.visitId,
                        visitLabel: `V${visit.visitNumber}`,
                        // Wireframe order: surname first (differs from the VCL target).
                        patientName: `${visit.patientLastName} ${visit.patientFirstName}`,
                      })
                  : undefined
              }
            />
            {registrationVisit !== null && procedureVisit === null && activeTrialId !== null && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setRegistrationVisit(null)}
                  className="mb-2 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  ← Powrót do planu dnia
                </button>
                <VisitRegistration
                  visitId={registrationVisit.visitId}
                  trialId={activeTrialId}
                  visitLabel={registrationVisit.visitLabel}
                  patientName={registrationVisit.patientName}
                  scheduledDate={registrationVisit.scheduledDate}
                  scheduledTime={registrationVisit.scheduledTime}
                  performedDate={registrationVisit.performedDate}
                  performedTime={registrationVisit.performedTime}
                  currentType={registrationVisit.currentType}
                  currentComment={registrationVisit.currentComment}
                  canWrite={canWriteVisits}
                  onOpenProcedures={
                    registrationDayPlanRow !== null
                      ? () => setProcedureVisit(registrationDayPlanRow)
                      : undefined
                  }
                  onSaved={() => {
                    setRegistrationVisit(null);
                    // The registration writes into BOTH read paths: the day plans
                    // (Domain 4) and the chart's plan history (Domain 3).
                    void queryClient.invalidateQueries({ queryKey: visitQueryKeys.all });
                    void queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
                  }}
                  onClose={() => setRegistrationVisit(null)}
                />
              </div>
            )}
            {procedureVisit !== null && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setProcedureVisit(null)}
                  className="mb-2 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  ← Powrót do rejestracji wizyty
                </button>
                <ProcedureRegistration
                  patientVisitId={procedureVisit.visitId}
                  trialId={activeTrialId}
                  visitLabel={`V${procedureVisit.visitNumber}`}
                  canRead={canReadVisits}
                  canWrite={canWriteVisits}
                  onSaved={() => setProcedureVisit(null)}
                />
              </div>
            )}
            {kitVisitTarget !== null && activeTrialId !== null && (
              <KitRegistrationDialog
                patientVisitId={kitVisitTarget.visitId}
                trialId={activeTrialId}
                visitLabel={kitVisitTarget.visitLabel}
                patientName={kitVisitTarget.patientName}
                patientCode={kitVisitTarget.patientCode}
                initialFlag={kitVisitTarget.initialFlag}
                canRead={canReadVisits}
                canWrite={canWriteVisits}
                onSaved={() => {
                  // Dialog already invalidated visit/patient/stock queries on success.
                  setKitVisitTarget(null);
                  setKitSavedNotice('Rejestracja kitów została zapisana');
                }}
                onOpenInventory={() => {
                  setKitVisitTarget(null);
                  setActiveTab('inventory');
                }}
                onClose={() => setKitVisitTarget(null)}
              />
            )}
            {vclVisitTarget !== null && (
              <VclPrintDialog
                target={vclVisitTarget}
                onClose={() => setVclVisitTarget(null)}
              />
            )}
            {patientTodoTarget !== null && (
              <PatientTodoPrintDialog
                target={patientTodoTarget}
                onClose={() => setPatientTodoTarget(null)}
              />
            )}
            {kitSavedNotice !== null && (
              <div
                role="status"
                data-testid="kit-saved-toast"
                className="fixed bottom-6 right-6 z-[70] rounded-md border border-emerald-700 bg-emerald-950/95 px-4 py-2 text-xs font-medium text-emerald-200 shadow-xl"
              >
                {kitSavedNotice}
              </div>
            )}
            <TrialParticipants
              trialId={activeTrialId}
              canRead={canReadPatients}
              onSelectPatient={setSelectedParticipantPatientId}
            />
            <PatientPanel
              canRead={canReadPatients}
              canWrite={canWritePatients}
              canScheduleVisits={canWriteVisits}
              canReadVisits={canReadVisits}
              activeTrialId={activeTrialId}
              selectedPatientId={selectedParticipantPatientId}
              onSelectPatient={setSelectedParticipantPatientId}
              onEnrollPatient={(trialId) => {
                if (activeTrialId === null) {
                  setActiveTrialId(trialId);
                }
              }}
              onRegisterKits={(visit, patient) =>
                setKitVisitTarget({
                  visitId: visit.id,
                  visitLabel: visit.label,
                  patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Pacjent',
                  patientCode: patient?.admissionBookNumber ? patient.admissionBookNumber : `#${visit.id}`,
                  initialFlag: visit.flag,
                })
              }
              onRegisterVisit={setRegistrationVisit}
            />
            {/* D4a — same site card reaches the patient-area dashboard too (variant A). */}
            <SiteStatusCard
              siteName={tenant?.name}
              siteId={user?.tenantId}
              canEdit={isTenantAdmin}
            />
          </div>
        )}

        {activeTab === 'dashboard' && !canReadPatients && (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">
                  Panel Główny Kokpitu
                </h2>
                <p className="text-xs text-slate-400">
                  Zarządzanie wizytami pacjentów, procedurami oraz magazynem kitów ośrodka
                </p>
              </div>
              <span className="text-xs text-slate-500 font-mono">
                Desktop 1920×1080 · Domena 1
              </span>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Site status card (D4a) — the working window lives on the card of the site itself. */}
              <SiteStatusCard
                siteName={tenant?.name}
                siteId={user?.tenantId}
                canEdit={isTenantAdmin}
              />

              {/* Identity & RBAC status card */}
              <section
                aria-label="Uprawnienia profilu"
                className="rounded-lg border border-slate-800 bg-slate-900 p-5 shadow-xs"
              >
                <div className="flex items-center gap-2 mb-3 text-cyan-400">
                  <Shield className="h-4 w-4" />
                  <h3 className="text-sm font-semibold text-slate-200">Uprawnienia i Bezpieczeństwo</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between border-b border-slate-800/80 pb-1.5">
                    <span className="text-slate-400">Rola konta:</span>
                    <span className="font-medium text-slate-200">
                      {isTenantAdmin ? 'Administrator Ośrodka' : 'Zwykły Użytkownik'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800/80 pb-1.5">
                    <span className="text-slate-400">Moduł finansowy:</span>
                    <span className={hasFinanceAccess ? 'text-emerald-400 font-medium' : 'text-slate-500'}>
                      {hasFinanceAccess ? 'Dostęp przyznany' : 'Dostęp zablokowany'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800/80 pb-1.5">
                    <span className="text-slate-400">Uwierzytelnianie 2FA:</span>
                    <span className="text-cyan-400 font-medium">Aktywne (TOTP)</span>
                  </div>
                </div>
              </section>

              {/* Service Health Card */}
              <section
                aria-label="Stan usług API"
                className="rounded-lg border border-slate-800 bg-slate-900 p-5 shadow-xs"
              >
                <div className="flex items-center gap-2 mb-3 text-cyan-400">
                  <ShieldAlert className="h-4 w-4" />
                  <h3 className="text-sm font-semibold text-slate-200">Usługi Platformy</h3>
                </div>
                {health.isPending ? (
                  <p data-testid="health-pending" className="text-xs text-slate-400">
                    Sprawdzanie usługi API…
                  </p>
                ) : health.isError ? (
                  <p data-testid="health-error" className="text-xs text-amber-400">
                    API niedostępne — upewnij się, że usługa apps/api jest uruchomiona.
                  </p>
                ) : (
                  <div>
                    <p data-testid="health-ok" className="text-xs text-emerald-400 font-medium">
                      API online: {health.data.service} v{health.data.version}
                    </p>
                    <p data-testid="build-info-detail" className="mt-1 text-[11px] text-slate-400 font-mono">
                      Front: {BUILD_INFO.tag} ({BUILD_INFO.commit})
                    </p>
                    <p className="mt-2 text-[11px] text-slate-400">
                      Połączenie z bazą PostgreSQL i szyfrowana sesja Better Auth aktywne.
                    </p>
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        {activeTab === 'trials' && (
          <TrialsView
            canRead={canReadTrials}
            canWrite={canWriteTrials}
            canWritePersonnel={canWritePersonnel}
            isTenantAdmin={isTenantAdmin}
          />
        )}

        {activeTab === 'contacts' && canReadPersonnel && (
          <ContactsCatalog canRead={canReadPersonnel} canWrite={canWritePersonnel} />
        )}

        {activeTab === 'users' && isTenantAdmin && <UsersView />}

        {activeTab === 'inventory' && activeTrialId !== null && (
          <KitInventoryView
            trialId={activeTrialId}
            canWrite={canWriteVisits}
            canWriteKits={canWriteKits}
            onPrintProtocol={
              canReadDocuments
                ? (protocol, labName) =>
                    setKitDestructionTarget({
                      protocolId: protocol.id,
                      protocolDate: protocol.protocolDate,
                      labName,
                    })
                : undefined
            }
          />
        )}

        {activeTab === 'inventory' && kitDestructionTarget !== null && (
          <KitDestructionPrintDialog
            target={kitDestructionTarget}
            onClose={() => setKitDestructionTarget(null)}
          />
        )}

        {activeTab === 'inventory' && activeTrialId === null && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400">
            <Boxes className="mx-auto h-8 w-8 text-cyan-400 mb-2" />
            <p className="text-sm font-medium text-slate-200">Wybierz aktywne badanie</p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Wybierz badanie z listy w nagłówku, aby przeglądać stan magazynowy i przyjmować dostawy.
            </p>
          </div>
        )}

        {activeTab === 'demand' && activeTrialId !== null && (
          <KitDemandForecastView trialId={activeTrialId} />
        )}

        {activeTab === 'demand' && activeTrialId === null && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400">
            <AlertTriangle className="mx-auto h-8 w-8 text-cyan-400 mb-2" />
            <p className="text-sm font-medium text-slate-200">Wybierz aktywne badanie</p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Wybierz badanie z listy w nagłówku, aby oszacować zapotrzebowanie i ostrzeżenia o
              brakach zestawów.
            </p>
          </div>
        )}

        {activeTab === 'finance' && hasFinanceAccess && (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-950 text-emerald-400 ring-1 ring-emerald-800">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-100">
                    Moduł Finansowy Ośrodka
                  </h2>
                  <p className="text-xs text-slate-400">
                    Zarządzanie cennikami procedur, budżetami badań i wynagrodzeniami personelu (Domena 6)
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400">
              <DollarSign className="mx-auto h-8 w-8 text-emerald-400 mb-2" />
              <p className="text-sm font-medium text-slate-200">
                Dostęp do finansów potwierdzony
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                Silnik precyzji finansowej NUMERIC(19,4) oraz rozliczenia wizyt zostaną
                dostarczone w dedykowanej Domenie 6 (Finance & Contracts).
              </p>
            </div>
          </div>
        )}
        {/* #7 A6: the loupe — picking a result sets the active trial (the
            recall save rides on handleSelectTrial). */}
        <TrialSearchDialog
          isOpen={trialSearchOpen}
          onSelect={(trialId) => handleSelectTrial(trialId)}
          onClose={() => setTrialSearchOpen(false)}
        />
        {/* #7 A5: the grid — the bar definition window (user-owned
            personalization; the shortcut edit inside is gated by trials:write). */}
        <BarManagerDialog
          isOpen={barManagerOpen}
          canWriteTrials={canWriteTrials}
          onClose={() => setBarManagerOpen(false)}
        />
        </main>
      </div>
    </div>
  );
}
